import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../utils/axiosInstance";

const IncomingQuestions = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replies, setReplies] = useState({});
  const [sending, setSending] = useState({});
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchComplaints = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/complaints");
      setComplaints(res.data);
    } catch {
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComplaints();
    const interval = setInterval(fetchComplaints, 5000);
    return () => clearInterval(interval);
  }, [fetchComplaints]);

  const handleReply = async (id) => {
    const answer = (replies[id] || "").trim();
    if (!answer) return;
    setSending((p) => ({ ...p, [id]: true }));
    try {
      await axiosInstance.put(`/complaints/${id}/reply`, { answer });
      setReplies((p) => ({ ...p, [id]: "" }));
      setToast({ type: "success", msg: "✅ Igisubizo cyoherejwe neza!" });
      fetchComplaints();
    } catch {
      setToast({ type: "danger", msg: "❌ Habaye ikosa. Ongera ugerageze." });
    } finally {
      setSending((p) => ({ ...p, [id]: false }));
      setTimeout(() => setToast(null), 4000);
    }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleString("rw-RW"); } catch { return d; }
  };

  const filtered = filter === "pending"
    ? complaints.filter((c) => c.status === "pending")
    : filter === "answered"
    ? complaints.filter((c) => c.status === "answered")
    : complaints;

  const pendingCount = complaints.filter((c) => c.status === "pending").length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2" style={{ color: "var(--primary)" }}>
            <i className="bi bi-envelope-open-fill"></i>
            Ibibazo by'Imidugudu
          </h2>
          <p className="text-muted mb-0 small">Subiza ibibazo by'abayobozi b'imidugudu.</p>
        </div>
        {pendingCount > 0 && (
          <span className="badge bg-warning text-dark fs-6 px-3 py-2">
            <i className="bi bi-clock me-1"></i>{pendingCount} itegereje igisubizo
          </span>
        )}
      </div>

      {toast && (
        <div className={`alert alert-${toast.type} alert-dismissible`}>
          {toast.msg}
          <button type="button" className="btn-close" onClick={() => setToast(null)}></button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[
          { key: "all", label: "Byose", icon: "bi-list-ul" },
          { key: "pending", label: "Bitegereje", icon: "bi-clock" },
          { key: "answered", label: "Byasubijwe", icon: "bi-check-circle" },
        ].map((f) => (
          <button
            key={f.key}
            className={`btn btn-sm ${filter === f.key ? "btn-primary" : "btn-outline-secondary"}`}
            style={{ borderRadius: 20 }}
            onClick={() => setFilter(f.key)}
          >
            <i className={`bi ${f.icon} me-1`}></i>{f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <p className="mt-2 text-muted">Birimo gutegurwa...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
            <p className="mt-3 text-muted fw-semibold">Nta bibazo bibonetse.</p>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {/* Show pending first */}
          {[...filtered]
            .sort((a, b) => (a.status === "pending" ? -1 : 1))
            .map((c) => (
              <div
                key={c.id}
                className="card border-0 shadow-sm"
                style={{ borderLeft: `4px solid ${c.status === "pending" ? "#ffc107" : "#198754"}` }}
              >
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                        style={{ width: 40, height: 40, background: "var(--primary, #003580)", fontSize: "0.85rem" }}
                      >
                        {(c.village_name || "U").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-bold">{c.village_name || "Umudugudu"}</div>
                        <small className="text-muted">{formatDate(c.created_at)}</small>
                      </div>
                    </div>
                    <span className={`badge ${c.status === "answered" ? "bg-success" : "bg-warning text-dark"}`}>
                      {c.status === "answered" ? "✅ Yasubijwe" : "⏳ Itegereje"}
                    </span>
                  </div>

                  {/* Question */}
                  <div className="mb-3">
                    <div className="fw-semibold text-muted small mb-1">
                      <i className="bi bi-question-circle me-1"></i>IKIBAZO:
                    </div>
                    <div className="p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      {c.question}
                    </div>
                  </div>

                  {/* Existing answer */}
                  {c.status === "answered" && c.answer && (
                    <div className="mb-3">
                      <div className="fw-semibold text-success small mb-1">
                        <i className="bi bi-chat-dots-fill me-1"></i>IGISUBIZO CYATANZWE:
                      </div>
                      <div className="p-3 rounded-3 border border-success" style={{ background: "#f0fff4" }}>
                        {c.answer}
                      </div>
                    </div>
                  )}

                  {/* Reply form — show for pending, or allow update for answered */}
                  {c.status === "pending" && (
                    <div>
                      <div className="fw-semibold text-primary small mb-2">
                        <i className="bi bi-reply-fill me-1"></i>SUBIZA:
                      </div>
                      <textarea
                        className="form-control mb-2"
                        rows={3}
                        placeholder="Andika igisubizo cyawe hano..."
                        value={replies[c.id] || ""}
                        onChange={(e) => setReplies((p) => ({ ...p, [c.id]: e.target.value }))}
                        style={{ resize: "vertical" }}
                      />
                      <button
                        className="btn btn-primary btn-sm px-4 fw-semibold"
                        style={{ borderRadius: 10 }}
                        disabled={sending[c.id] || !(replies[c.id] || "").trim()}
                        onClick={() => handleReply(c.id)}
                      >
                        {sending[c.id] ? (
                          <><span className="spinner-border spinner-border-sm me-2"></span>Birimo koherezwa...</>
                        ) : (
                          <><i className="bi bi-send-fill me-2"></i>Ohereza Igisubizo</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default IncomingQuestions;
